import React, { useState, useEffect } from 'react';
import axios from 'axios';

import { useSocketContext } from '@techchange/components';

const axiosClient = axios.create({
	withCredentials: true,
});

/**
 * React hook that keeps track of the total number of a user's unread notifications.
 *
 * @param {Object} params                    The base query params.
 * @param {String} params.user_id            The user id.
 * @param {String} params.organization_id    The organization id.
 *
 * @returns {Array} The unread count data and a callback function to set the unread count state.
 */
const useNotificationsUnreadCount = (params = {}) => {

	const [unreadCount, setUnreadCount] = useState(null);

	const { socket } = useSocketContext();

	useEffect(() => {

		const fetchData = () => {

			axiosClient.get(`/api/v2/notifications/unread-count/${params.user_id}`).then((response) => {

				setUnreadCount(response && response.data);

			}).catch((err) => {

				console.error(err);

			});

		};

		if (params.user_id && !unreadCount) {
			fetchData();
		}

	}, [params, unreadCount]);

	// Listen for real-time changes to the unread notifications count
	useEffect(() => {

		const handleRealTimeUpdate = (notification) => {

			// Check that the new notification is for the current user
			if (notification.user_id !== params.user_id ||
				notification.organization_id !== params.organization_id) {
				return;
			}

			setUnreadCount((prev) => {

				if (!prev) {
					return prev;
				}

				return { count: prev.count + 1 };

			});

		};

		// Use a socket to start listening for real-time changes
		socket?.on('create:notification', handleRealTimeUpdate);

		// Clean up the socket to stop listening for real-time changes
		return () => socket?.removeListener('create:notification', handleRealTimeUpdate);

	}, [socket, params.user_id, params.organization_id]);

	return [unreadCount, setUnreadCount];

};

export default useNotificationsUnreadCount;
